import { ReactElement, useCallback, useMemo } from 'react';
import { Box } from '@mui/material';

import successImage from 'modules/common/assets/unstake-success.png';
import { SuccessDialogContent } from 'modules/common/components/SuccessDialogContent';
import { DEFAULT_DECIMAL_PLACES } from 'modules/common/const';
import { KnownDialogs, useDialog } from 'modules/dialogs';
import { Dialog } from 'modules/dialogs/components/Dialog';
import { useGlobalTranslation } from 'modules/i18n';
import { useGetLiquidUnstakePeriodQuery } from 'modules/liquidStaking/actions/getLiquidUnstakePeriod';
import { UnstakeType } from 'modules/liquidStaking/const';
import { IModalUnstakeSuccessState } from 'modules/liquidStaking/types';

import { translation } from './translation';

interface IUnstakeSuccessDialogProps {
  onClose?: () => void;
}

export function UnstakeSuccessDialog({
  onClose,
}: IUnstakeSuccessDialogProps): ReactElement {
  const { t, keys } = useGlobalTranslation(translation);
  const {
    isOpened,
    onClose: onCloseModal,
    context,
  } = useDialog<IModalUnstakeSuccessState>(KnownDialogs.tabUnstakeSuccess);

  const { inAmount, outAmount, txHash, type } = context || {};

  const handleClose = useCallback(() => {
    onClose?.();
    onCloseModal();
  }, [onClose, onCloseModal]);

  const { data: days } = useGetLiquidUnstakePeriodQuery();

  const inAmountStr = inAmount
    ?.decimalPlaces(DEFAULT_DECIMAL_PLACES)
    .toFormat();
  const outAmountStr = outAmount
    ?.decimalPlaces(DEFAULT_DECIMAL_PLACES)
    .toFormat();
  const description = useMemo(() => {
    if (type === UnstakeType.Unstake) {
      if (!days) {
        return '';
      }

      return t(
        keys.doneDescription,
        {
          period: days.toNumber(),
          inAmount: inAmountStr,
          outAmount: outAmountStr,
          stakeToken: t(keys.tokens.stakeToken),
          mainToken: t(keys.tokens.main),
        },
        true,
      );
    }

    return t(
      keys.doneFlashDescription,
      {
        inAmount: inAmountStr,
        outAmount: outAmountStr,
        stakeToken: t(keys.tokens.stakeToken),
        mainToken: t(keys.tokens.main),
      },
      true,
    );
  }, [
    type,
    t,
    keys.doneFlashDescription,
    keys.tokens.stakeToken,
    keys.tokens.main,
    keys.doneDescription,
    inAmountStr,
    outAmountStr,
    days,
  ]);

  return (
    <Dialog open={isOpened} onClose={handleClose}>
      <Box
        sx={theme => ({
          display: 'flex',
          flexDirection: 'column',
          minHeight: 487,

          [theme.breakpoints.down('md')]: { minHeight: 475 },
        })}
      >
        <SuccessDialogContent
          buttonLabel={t(keys.buttonText)}
          description={description}
          image={successImage}
          title={t(keys.done)}
          txHash={txHash}
          onClose={handleClose}
        />
      </Box>
    </Dialog>
  );
}
